import { Injectable } from '@angular/core';
import { Asset, AssetsParams, LiveApiAsset } from '@app/modules/location-client/location-api.models';
import { LocationApiService } from '@app/modules/location-client/location-api.service';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectCurrentFilter } from '@app/store/filters/selectors/filters.selectors';
import { DataDogService } from './data-dog.service';
import { convertToAsset } from '@app/modules/location-client/utilities';
@Injectable({
  providedIn: 'root'
})
export class LiveAssetQueryService {
  constructor(private locationApi: LocationApiService, private store: Store, private datadog: DataDogService) {}

  executeAssetQueryWithParams(params: AssetsParams): Observable<Array<Asset>> {
    return this.fetchAssets(params).pipe(
      map((results: LiveApiAsset[]) => {
        const res = this.createResultsFromAPIRequests(results);
        if (!res?.length) {
          return [];
        }
        return res.map(convertToAsset);
      }),
      catchError((error, _) => {
        return EMPTY;
      })
    );
  }

  // TODO remove that if we don't use pagination
  private createResultsFromAPIRequests(results) {
    const resultSet = new Map<string, LiveApiAsset>();
    // If we only had to make a single paginated request flow
    // to the Location API, then we do not need to flatten the array of arrays.
    if (!Array.isArray(results[0])) {
      results.forEach((item: LiveApiAsset) => {
        if (!resultSet.has(item.id)) {
          resultSet.set(item.id, item);
        }
      });
    } else {
      // Otherwise, we flatten.
      results.forEach(res =>
        res.map((item: LiveApiAsset) => {
          if (!resultSet.has(item.id)) {
            resultSet.set(item.id, item);
          }
        })
      );
    }
    return Array.from(resultSet.values());
  }

  /**
   * Returns the set of API results | Assets based on parameters for Zonar users
   * @param params
   */
  fetchAssets(params: AssetsParams): Observable<any[]> {
    const isFilteredByLocation = params?.divisionIds?.length;
    // const isFilteredByAccount = params.accountDivisions?.length;
    if (isFilteredByLocation) {
      const divParams = { ...params, divisionIds: params.divisionIds };
      return this.fetchPaginatedAssets(divParams);
    } else {
      // Or failing any parameters we will grab all the things based on the params.
      return this.fetchPaginatedAssets(params);
    }
  }

  /**
   * Performs pagination flow based on parameters and headers. Will fetch from Location API
   * until there is no longer a next page token
   * @param params
   */

  // TODO rename if we don't use pagination
  private fetchPaginatedAssets(params: AssetsParams): Observable<Array<LiveApiAsset>> {
    let currentFilter;
    this.store.select(selectCurrentFilter).subscribe(filter => {
      currentFilter = filter;
    });

    const startFetchPaginated = Date.now().toString(); // gives mark a unique name in case of overlapping requests
    performance.mark(startFetchPaginated);

    return this.locationApi.getAssets(params).pipe(
      tap(() => {
        const fetchPagDur = Date.now().toString();
        performance.measure(fetchPagDur, startFetchPaginated);
        this.datadog.addRumTiming('paginated_assets_loaded', performance.getEntriesByName(fetchPagDur)[0].duration);
        performance.clearMarks(startFetchPaginated);
        performance.clearMeasures(fetchPagDur);
      }),
      catchError((err, _) => EMPTY)
    );
  }
}
